import { Suspense, lazy, useMemo, useState } from "react";
import {
  Anchor,
  Card,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Link, useSearchParams } from "react-router";
import {
  useActiveMiningCount,
  useBlockHead,
  useDomainDetail,
  useDomainLeaderboard,
  useDomainRegistrationsSeries,
  useNetworkMiningCostSeries,
} from "../api";
import { shortenHex } from "../identity";
import { formatCog } from "../units";
import type { OffChainCostModel } from "../economics/miningFloor";
import { ErrorAlert } from "../components/ErrorAlert";

const RegistrationsChart = lazy(() =>
  import("../charts/RegistrationsChart").then((m) => ({ default: m.RegistrationsChart })),
);

const MiningCostFloorChart = lazy(() =>
  import("../charts/MiningCostFloorChart").then((m) => ({
    default: m.MiningCostFloorChart,
  })),
);

const WINDOW_OPTIONS = [
  { label: "24h", value: "144" },
  { label: "7d", value: "1008" },
  { label: "30d", value: "4320" },
] as const;

const DEFAULT_WINDOW = "144";

export function DomainsOverview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const windowParam = searchParams.get("window") ?? DEFAULT_WINDOW;
  const windowBlocks = isWindowOption(windowParam) ? Number(windowParam) : Number(DEFAULT_WINDOW);

  const head = useBlockHead();
  const toHeight = head.data ? Number(head.data.height) : null;

  const series = useDomainRegistrationsSeries(toHeight);
  const activeMining = useActiveMiningCount(windowBlocks);
  const leaderboard = useDomainLeaderboard(toHeight, { limit: 10 });
  const costSeries = useNetworkMiningCostSeries(toHeight);
  const latestWinner = head.data?.latestWinner ?? null;
  const winnerDomainId = latestWinner?.miningDomainId ?? null;
  const winnerDomainDetail = useDomainDetail(winnerDomainId, toHeight);

  const totalDomains = useMemo(() => {
    if (!series.data || series.data.entries.length === 0) return null;
    const last = series.data.entries[series.data.entries.length - 1];
    return last ? last.cumulativeTotal : null;
  }, [series.data]);

  // Inputs the chain can't know — kept in local session state (they
  // don't change which cached endpoint is fetched, so they aren't
  // navigational filters and don't belong in the URL or storage).
  const [btcUsd, setBtcUsd] = useState<number | string>("");
  const [modelKind, setModelKind] = useState<OffChainCostModel["kind"]>("none");
  const [perSubmissionUsd, setPerSubmissionUsd] = useState<number | string>("");
  const [excludeGateFails, setExcludeGateFails] = useState(false);
  const [multiplierFactor, setMultiplierFactor] = useState<number | string>("");

  const offChainModel = useMemo<OffChainCostModel>(() => {
    if (modelKind === "perSubmission") {
      return {
        kind: "perSubmission",
        usd: typeof perSubmissionUsd === "number" ? perSubmissionUsd : 0,
        excludeGateFails,
      };
    }
    if (modelKind === "multiplier") {
      return {
        kind: "multiplier",
        factor: typeof multiplierFactor === "number" ? multiplierFactor : 0,
      };
    }
    return { kind: "none" };
  }, [modelKind, perSubmissionUsd, excludeGateFails, multiplierFactor]);

  const btcUsdRate = typeof btcUsd === "number" ? btcUsd : 0;

  return (
    <Stack gap="lg">
      <Title order={2}>Domains</Title>

      <Group grow align="stretch">
        <SummaryCard
          label="Domains registered"
          value={totalDomains === null ? null : totalDomains.toString()}
          isLoading={series.isPending}
          error={series.error}
        />
        <SummaryCard
          label={`Actively mining (last ${WINDOW_OPTIONS.find((o) => o.value === windowParam)?.label ?? "window"})`}
          value={
            activeMining.data === undefined ? null : activeMining.data.activeCount.toString()
          }
          isLoading={activeMining.isPending}
          error={activeMining.error}
          extra={
            <SegmentedControl
              size="xs"
              value={windowParam}
              data={WINDOW_OPTIONS as unknown as { label: string; value: string }[]}
              onChange={(value) => {
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    if (value === DEFAULT_WINDOW) next.delete("window");
                    else next.set("window", value);
                    return next;
                  },
                  { replace: true },
                );
              }}
            />
          }
        />
      </Group>

      <Card withBorder padding="md">
        <Stack gap="xs">
          <Title order={4}>Latest block winner</Title>
          {head.isPending ? <Loader size="sm" /> : null}
          {head.isError ? <ErrorAlert error={head.error} /> : null}
          {head.isSuccess && latestWinner === null ? (
            <Text c="dimmed">No winning blocks yet.</Text>
          ) : null}
          {latestWinner ? (
            <Group gap="xl" wrap="wrap" align="flex-start">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  Block
                </Text>
                <Text>{latestWinner.height.toString()}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  Domain
                </Text>
                <Anchor
                  component={Link}
                  to={`/domain/${latestWinner.miningDomainId.toString()}`}
                  underline="hover"
                >
                  <Group gap={6} wrap="nowrap">
                    {winnerDomainDetail.data?.name ? (
                      <Text component="span">{winnerDomainDetail.data.name}</Text>
                    ) : null}
                    <Text component="span" c="dimmed" size="sm">
                      #{latestWinner.miningDomainId}
                    </Text>
                  </Group>
                </Anchor>
              </Stack>
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  Recipient wallet
                </Text>
                <Anchor
                  component={Link}
                  to={`/wallet/${latestWinner.recipientScriptPubKey}`}
                  underline="hover"
                  ff="monospace"
                  data-testid="latest-winner-recipient-link"
                >
                  {shortenHex(latestWinner.recipientScriptPubKey, 12, 8)}
                </Anchor>
              </Stack>
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  Reward
                </Text>
                <Text>
                  {formatCog(latestWinner.rewardCogtoshi, {
                    groupIntegerDigits: true,
                    minDecimals: 2,
                  })}{" "}
                  COG
                </Text>
              </Stack>
            </Group>
          ) : null}
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="xs">
          <Title order={4}>Cumulative domain registrations</Title>
          {series.isPending ? <Loader size="sm" /> : null}
          {series.isError ? <ErrorAlert error={series.error} /> : null}
          {series.data?.entries.length ? (
            <Suspense fallback={<Loader size="sm" />}>
              <RegistrationsChart entries={series.data.entries} />
            </Suspense>
          ) : null}
          {series.data?.entries.length === 0 ? (
            <Text c="dimmed">No registrations yet.</Text>
          ) : null}
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="xs">
          <Title order={4}>Minimum value of COG (mining cost floor)</Title>
          <Text size="sm" c="dimmed">
            The cost of producing COG is a lower bound on its value. The API
            ships the on-chain legs; you supply a flat BTC/USD rate and an
            optional off-chain cost model. Approximate — it values all
            historical mining cost at the single rate you enter.
          </Text>

          <Group align="flex-end" gap="md" wrap="wrap">
            <NumberInput
              label="BTC / USD"
              prefix="$"
              thousandSeparator=","
              min={0}
              value={btcUsd}
              onChange={setBtcUsd}
              w={180}
              data-testid="floor-btc-usd"
            />
            <Select
              label="Off-chain cost"
              w={200}
              allowDeselect={false}
              value={modelKind}
              onChange={(value) => {
                if (value === "perSubmission" || value === "multiplier") {
                  setModelKind(value);
                } else {
                  setModelKind("none");
                }
              }}
              data={[
                { value: "none", label: "None" },
                { value: "perSubmission", label: "USD per submission" },
                { value: "multiplier", label: "× on-chain cost" },
              ]}
            />
            {modelKind === "perSubmission" ? (
              <>
                <NumberInput
                  label="USD / submission"
                  prefix="$"
                  min={0}
                  value={perSubmissionUsd}
                  onChange={setPerSubmissionUsd}
                  w={160}
                />
                <Switch
                  label="Exclude gate-fails"
                  checked={excludeGateFails}
                  onChange={(e) => {
                    setExcludeGateFails(e.currentTarget.checked);
                  }}
                />
              </>
            ) : null}
            {modelKind === "multiplier" ? (
              <NumberInput
                label="× on-chain cost"
                min={0}
                step={0.1}
                value={multiplierFactor}
                onChange={setMultiplierFactor}
                w={160}
              />
            ) : null}
          </Group>

          {costSeries.isPending ? <Loader size="sm" /> : null}
          {costSeries.isError ? <ErrorAlert error={costSeries.error} /> : null}
          {costSeries.isSuccess && costSeries.data.entries.length === 0 ? (
            <Text c="dimmed">No mining activity yet.</Text>
          ) : null}
          {costSeries.data?.entries.length && btcUsdRate <= 0 ? (
            <Text c="dimmed">Enter a BTC/USD rate to see the floor.</Text>
          ) : null}
          {costSeries.data?.entries.length && btcUsdRate > 0 ? (
            <Suspense fallback={<Loader size="sm" />}>
              <MiningCostFloorChart
                entries={costSeries.data.entries}
                btcUsd={btcUsdRate}
                model={offChainModel}
              />
            </Suspense>
          ) : null}
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="xs">
          <Title order={4}>Top domains by rewards</Title>
          {leaderboard.isPending ? <Loader size="sm" /> : null}
          {leaderboard.isError ? <ErrorAlert error={leaderboard.error} /> : null}
          {leaderboard.data ? <LeaderboardTable rows={leaderboard.data.entries} /> : null}
        </Stack>
      </Card>
    </Stack>
  );
}

function isWindowOption(s: string): s is (typeof WINDOW_OPTIONS)[number]["value"] {
  return WINDOW_OPTIONS.some((o) => o.value === s);
}

interface SummaryCardProps {
  readonly label: string;
  readonly value: string | null;
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly extra?: React.ReactNode;
}

function SummaryCard({ label, value, isLoading, error, extra }: SummaryCardProps) {
  return (
    <Card withBorder padding="md">
      <Stack gap={4}>
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            {label}
          </Text>
          {extra}
        </Group>
        {isLoading ? <Loader size="sm" /> : null}
        {error ? <ErrorAlert error={error} /> : null}
        {value !== null && !isLoading && !error ? (
          <Text fz={32} fw={600}>
            {value}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

interface LeaderboardTableProps {
  readonly rows: readonly {
    readonly domainId: number;
    readonly name: string | null;
    readonly wins: number;
    readonly cogtoshiWon: bigint;
  }[];
}

function LeaderboardTable({ rows }: LeaderboardTableProps) {
  if (rows.length === 0) {
    return <Text c="dimmed">No rewarded domains yet.</Text>;
  }
  return (
    <Table withTableBorder striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>#</Table.Th>
          <Table.Th>Domain</Table.Th>
          <Table.Th>Wins</Table.Th>
          <Table.Th>COG won</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, index) => (
          <Table.Tr key={row.domainId}>
            <Table.Td>{index + 1}</Table.Td>
            <Table.Td>
              <Anchor
                component={Link}
                to={`/domain/${row.domainId.toString()}`}
                underline="hover"
              >
                <Group gap={6} wrap="nowrap">
                  <Text>
                    {row.name ?? (
                      <Text component="span" c="dimmed">
                        (unknown)
                      </Text>
                    )}
                  </Text>
                  <Text size="xs" c="dimmed">
                    #{row.domainId}
                  </Text>
                </Group>
              </Anchor>
            </Table.Td>
            <Table.Td>{row.wins}</Table.Td>
            <Table.Td>
              {formatCog(row.cogtoshiWon, { groupIntegerDigits: true, minDecimals: 2 })}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

